import { useState, useCallback, useEffect, useRef } from "react";

import type { FlowAnswers } from "@/shared/constants/flowTypes";

export function useJsonListAnswer<T>(
  answers: FlowAnswers,
  key: string,
  setAnswer: (key: string, value: string) => void,
  defaultValue: T[] = [],
): [T[], (updater: T[] | ((prev: T[]) => T[])) => void] {
  const [list, setList] = useState<T[]>(() => {
    try {
      const raw = answers[key];
      if (typeof raw === "string" && raw !== "[]") return JSON.parse(raw) as T[];
    } catch {
      // ignore
    }
    return defaultValue;
  });

  const lastWrittenRef = useRef<string>("");

  const sync = useCallback(
    (updater: T[] | ((prev: T[]) => T[])) => {
      setList((prev) => {
        const next = typeof updater === "function" ? (updater as (p: T[]) => T[])(prev) : updater;
        const serialized = JSON.stringify(next);
        lastWrittenRef.current = serialized;
        setAnswer(key, serialized);
        return next;
      });
    },
    [key, setAnswer],
  );

  useEffect(() => {
    const raw = answers[key];
    if (typeof raw !== "string" || raw === lastWrittenRef.current) return;
    try {
      setList(raw ? (JSON.parse(raw) as T[]) : []);
    } catch {
      // ignore
    }
  }, [answers, key]);

  return [list, sync];
}
